import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  PendingAttachFiles,
  type PendingAttachFilesHandle,
} from '@/components/shared'
import {
  FormAccordion,
  FormAccordionCard,
  FormInnerBlock,
  FormNeutralBadge,
} from '@/components/contracts/ContractFormAccordion'
import { contractsApi } from '@/services/contracts'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { RENEWAL_DEADLINE_CARDS } from './constants'
import { FormField, FrequencyCard, RequiredLabel } from './FormControls'
import type { Milestone } from './types'

interface Props {
  autoRenew: boolean
  setAutoRenew: (value: boolean) => void
  milestone: Milestone
  setMilestone: (value: Milestone) => void
  renewalNewStart: Date | null
  renewalNewEnd: Date | null
  requireSignature: boolean
  setRequireSignature: (value: boolean) => void
  editing: boolean
  contractId?: string
  clientId: string
  pendingAttachments: RefObject<PendingAttachFilesHandle | null>
}

export function FinalizationSection({
  autoRenew,
  setAutoRenew,
  milestone,
  setMilestone,
  renewalNewStart,
  renewalNewEnd,
  requireSignature,
  setRequireSignature,
  editing,
  contractId,
  clientId,
  pendingAttachments,
}: Props) {
  const { t } = useTranslation('contracts')

  return (
    <FormAccordionCard
      value='finalization'
      step={4}
      title={t('form.sections.finalization')}
      stepClassName='font-medium'
      titleClassName='font-semibold normal-case'
      badge={
        <FormNeutralBadge className='font-medium'>
          {t('form.renewalBadge')}
        </FormNeutralBadge>
      }
    >
      <FormAccordion
        defaultValue={['renewal-signature', 'attachments']}
        className='flex flex-col gap-4'
      >
        <FormInnerBlock
          value='renewal-signature'
          title={t('form.sections.renewalSignature')}
        >
          <div className='flex w-full flex-col gap-3'>
            <div className='flex w-full items-center gap-3'>
              <p className='min-w-0 flex-1 text-sm font-medium normal-case leading-[1.4] text-[#171a20]'>
                {t('form.renewal.requireRenewal')}
              </p>
              <Switch
                checked={!autoRenew}
                onCheckedChange={v => setAutoRenew(!v)}
                className={cn(
                  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#e7e9ef] [&>span]:size-5 [&>span]:data-[state=checked]:translate-x-4'
                )}
              />
            </div>
            <div className='text-xs font-normal normal-case leading-4 text-[#3d434e]'>
              <p>
                <span className='text-sm font-medium leading-[1.4] text-[#047857]'>
                  {t('form.renewal.requireRenewalHintOnPrefix')}
                </span>
                {t('form.renewal.requireRenewalHintOn')}
              </p>
              <p>
                <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                  {t('form.renewal.requireRenewalHintOffPrefix')}
                </span>
                {t('form.renewal.requireRenewalHintOff')}
              </p>
            </div>
          </div>

          {!autoRenew && (
            <div className='flex w-full flex-col gap-3 rounded-2xl border border-[#e7e9ef] bg-[#f4f5f8] p-4'>
              <FormField gap='md'>
                <RequiredLabel compact className='font-medium'>
                  {t('form.renewal.deadlineLabel')}
                </RequiredLabel>
                <div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-3'>
                  {RENEWAL_DEADLINE_CARDS.map(opt => (
                    <FrequencyCard
                      key={opt.value}
                      selected={milestone === opt.value}
                      onSelect={() => setMilestone(opt.value)}
                      title={t(`form.renewal.deadlineTitles.${opt.titleKey}`)}
                      hint={t(`form.renewal.deadlineHints.${opt.titleKey}`)}
                      titleClassName='text-sm font-medium normal-case'
                      hintClassName='text-xs normal-case leading-4'
                    />
                  ))}
                </div>
              </FormField>
              <div className='flex flex-wrap gap-4 rounded border border-dashed border-[#e7e9ef] bg-[#fbfbfc] px-4 py-3'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium normal-case leading-[1.4] text-[#3d434e]'>
                    {t('form.renewal.newStartDate')}
                  </span>
                  <span className='text-sm font-medium normal-case leading-[1.4] text-[#171a20]'>
                    {renewalNewStart
                      ? formatDate(renewalNewStart.toISOString())
                      : '-'}
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium normal-case leading-[1.4] text-[#3d434e]'>
                    {t('form.renewal.newEndDate')}
                  </span>
                  <span className='text-sm font-medium normal-case leading-[1.4] text-[#171a20]'>
                    {renewalNewEnd
                      ? formatDate(renewalNewEnd.toISOString())
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <label className='flex cursor-pointer items-center gap-3'>
            <Checkbox
              checked={requireSignature}
              onCheckedChange={v => setRequireSignature(!!v)}
              className={cn(
                'size-4 rounded-[4px] border-[#e7e9ef] data-[state=checked]:border-[#10b981] data-[state=checked]:bg-[#10b981] data-[state=checked]:text-white'
              )}
            />
            <span className='text-sm font-medium normal-case leading-[1.4] text-[#171a20]'>
              {t('form.renewal.requireSignature')}
            </span>
          </label>
        </FormInnerBlock>

        <FormInnerBlock
          value='attachments'
          title={t('form.sections.attachments')}
        >
          <PendingAttachFiles
            attachTo='contract'
            ref={pendingAttachments}
            entityId={editing ? contractId : undefined}
            clientId={clientId || undefined}
            uploadFn={contractsApi.uploadDocument}
            attachExistingFn={contractsApi.attachExistingDocument}
            listExistingFn={contractsApi.documents}
            removeFn={contractsApi.deleteDocument}
            renameFn={contractsApi.renameDocument}
            hideHeading
            brandTabs
            uploadTabLabel={t('form.uploadNew')}
          />
        </FormInnerBlock>
      </FormAccordion>
    </FormAccordionCard>
  )
}
